import { Injectable } from '@angular/core';
import {
  MinutesOfMeetingDetails,
  AttendanceTableMember,
  MeetingItem
} from 'src/app/shared/models/app.models';

@Injectable({
  providedIn: 'root'
})
export class WordExportService {

  async exportWord(minutesDetails: MinutesOfMeetingDetails): Promise<void> {
    if (!minutesDetails) {
      throw new Error('No meeting details available to export');
    }

    try {
      // Build Word content first
      const wordContent = this.buildWordContent(minutesDetails);

      if (!wordContent || wordContent.trim().length === 0) {
        throw new Error('No content available to export');
      }

      // Create container for Word content (hidden completely)
      const printContainer = document.createElement('div');
      printContainer.id = 'content-to-print';

      // Make container completely hidden
      printContainer.style.position = 'absolute';
      printContainer.style.left = '-9999px';
      printContainer.style.top = '-9999px';
      printContainer.style.width = '210mm';
      printContainer.style.minHeight = '297mm';
      printContainer.style.backgroundColor = 'white';
      printContainer.style.zIndex = '-1';
      printContainer.style.visibility = 'hidden';
      printContainer.style.opacity = '0';
      printContainer.style.display = 'none';
      printContainer.style.overflow = 'hidden';
      printContainer.style.pointerEvents = 'none';

      // Inject Word styles FIRST (before content)
      this.injectWordStyles(printContainer);

      // Set content
      printContainer.innerHTML = wordContent;

      // Append to body (hidden)
      document.body.appendChild(printContainer);
      
      // Temporarily show for rendering (but keep it off-screen)
      printContainer.style.display = 'block';
      printContainer.style.position = 'absolute';
      printContainer.style.left = '-9999px';
      printContainer.style.top = '-9999px';
      printContainer.style.visibility = 'hidden';

      // Force browser to render the content by accessing layout properties
      const height = printContainer.offsetHeight;
      const width = printContainer.offsetWidth;

      // Wait for fonts to load
      await document.fonts.ready;

      // Wait for content to render and styles to apply (reduced delay)
      await new Promise(resolve => setTimeout(resolve, 300));

      // Wait for images to load and convert to base64
      await this.convertImagesToBase64(printContainer);

      // Additional wait to ensure everything is rendered (reduced delay)
      await new Promise(resolve => setTimeout(resolve, 200));

      // Verify content is actually there
      const hasContent = printContainer.children.length > 0 && printContainer.scrollHeight > 0;
      if (!hasContent) {
        console.error('Word container is empty!', {
          children: printContainer.children.length,
          height: printContainer.scrollHeight,
          contentLength: wordContent.length
        });
        if (document.body.contains(printContainer)) {
          document.body.removeChild(printContainer);
        }
        throw new Error('Failed to generate Word content');
      }

      // Get the styles
      const styles = this.getWordStyles();

      // Convert HTML to Word document using dynamic import
      const htmlDocxJs = await import('html-docx-js-typescript');
      const asBlob = htmlDocxJs.asBlob || (htmlDocxJs as any).default?.asBlob;

      if (!asBlob || typeof asBlob !== 'function') {
        throw new Error('html-docx-js-typescript library is not loaded correctly');
      }

      // Remove any existing style tags from innerHTML to avoid duplication
      let cleanContent = printContainer.innerHTML;
      cleanContent = cleanContent.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

      // Wrap HTML content in proper structure for Word with inline styles
      const fullHtml = `<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns:m="http://schemas.microsoft.com/office/2004/12/omml">
<head>
  <meta charset="UTF-8">
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
  <meta name="ProgId" content="Word.Document">
  <meta name="Generator" content="Microsoft Word">
  <meta name="Originator" content="Microsoft Word">
  <!--[if gte mso 9]><xml>
   <w:WordDocument>
    <w:View>Print</w:View>
    <w:Zoom>100</w:Zoom>
    <w:DoNotOptimizeForBrowser/>
   </w:WordDocument>
  </xml><![endif]-->
  <style type="text/css">
${styles}
  </style>
</head>
<body style="margin: 0; padding: 0; font-family: 'Roboto', 'Cairo', sans-serif;">
${cleanContent}
</body>
</html>`;

      const result = await asBlob(fullHtml);
      
      // In browser context, asBlob returns Blob (Buffer is Node.js only)
      const blob = result as Blob;

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `MOM_${minutesDetails.basicInfo.meetingId}_${new Date().getTime()}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Cleanup
      if (document.body.contains(printContainer)) {
        document.body.removeChild(printContainer);
      }
      // Remove style from head
      const headStyle = document.getElementById('word-export-head-styles');
      if (headStyle && headStyle.parentNode) {
        headStyle.parentNode.removeChild(headStyle);
      }
    } catch (error) {
      console.error('Error during Word export:', error);
      throw error;
    }
  }

  private injectWordStyles(container: HTMLElement): void {
    // Load Google Fonts first
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&family=Roboto:wght@400;500;700&display=swap';
    document.head.appendChild(fontLink);

    // Create and inject style element
    const style = document.createElement('style');
    style.id = 'word-export-styles';
    style.textContent = this.getWordStyles();

    // Add to container
    container.appendChild(style);

    // Also add to document head to ensure styles are available
    const headStyle = document.createElement('style');
    headStyle.id = 'word-export-head-styles';
    headStyle.textContent = this.getWordStyles();
    document.head.appendChild(headStyle);
  }

  private getWordStyles(): string {
    return `
      * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }
      
      body {
        font-family: 'Roboto', 'Cairo', sans-serif;
        margin: 0;
        padding: 0;
        background-color: white;
        font-size: 14px;
      }
      
      .page {
        width: 210mm;
        min-height: 297mm;
        padding: 3mm 15mm 10mm 15mm;
        margin: 0 auto;
        background: white;
        border: none;
        box-shadow: none;
        position: relative;
        font-family: 'Roboto', 'Cairo', sans-serif;
        font-size: 14px;
      }
      
      .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 5px;
        margin-top: 0;
      }
      
      .logo img {
        max-height: 48px;
        height: 85px;
        max-width: 130px;
        width: 130px;
        display: block;
      }
      
      .header-meta {
        text-align: right;
        color: blue;
        font-weight: bold;
        font-size: 14px;
      }
      
      .main-title {
        text-align: center;
        font-weight: bold;
        margin: 5px 0;
        font-size: 16px;
      }
      
      .item-box {
        border: 2px solid #000;
        margin-bottom: 5px;
      }
      
      .item-header {
        background-color: #f0f0f0;
        padding: 5px;
        font-weight: bold;
        border-bottom: 2px solid #000;
      }
      
      .item-content {
        min-height: 150px;
        display: flex;
      }
      
      .item-content > div {
        flex: 1;
        padding: 10px;
      }
      
      .item-content > div:first-child {
        border-right: 2px solid #000;
      }

      .decision-rich-text {
        line-height: 1.6;
        margin: 0;
        word-break: break-word;
      }

      .decision-rich-text p,
      .decision-rich-text div,
      .decision-rich-text ol,
      .decision-rich-text ul {
        margin-top: 0;
        margin-bottom: 6px;
      }

      .decision-rich-text ol,
      .decision-rich-text ul {
        padding-left: 22px;
      }

      div[style*="direction: rtl"] .decision-rich-text ol,
      div[style*="direction: rtl"] .decision-rich-text ul {
        padding-right: 22px;
        padding-left: 0;
      }

      .decision-rich-text li {
        margin-bottom: 3px;
      }
      
      .section-title {
        text-align: center;
        margin: 10px 0;
        font-size: 15px;
      }
      
      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
        font-size: 13px;
      }
      
      th, td {
        border: 1px solid #000;
        padding: 5px;
        vertical-align: middle;
      }
      
      .meeting-details th {
        background-color: #e6e6e6;
        width: 20%;
        text-align: left;
      }
      
      .meeting-details td {
        width: 30%;
      }
      
      .bilingual-label {
        display: flex;
        justify-content: space-between;
      }
      
      .attendance-header {
        background-color: #0c1c3e;
        color: white;
        padding: 5px;
        font-weight: bold;
        border: 1px solid #000;
        border-bottom: none;
      }
      
      .attendance-table th {
        background-color: #e6e6e6;
        text-align: center;
        font-weight: bold;
      }
      
      .attendance-table td {
        text-align: center;
        height: 25px;
      }
      
      .attendance-table td:nth-child(1) {
        text-align: left;
        font-weight: bold;
      }
      
      .attendance-table td:nth-child(6) {
        text-align: right;
      }
      
      .attendance-table td:last-child {
        width: 40px;
      }
      
      .footer {
        position: absolute;
        bottom: 5mm;
        left: 15mm;
        right: 15mm;
        display: flex;
        justify-content: center;
        font-size: 12px;
        color: #666;
        border-top: 1px solid #ccc;
        padding-top: 5px;
        align-items: center;
        text-align: center;
        gap: 20px;
      }
      
      .page-last {
        page-break-after: auto;
      }
    `;
  }

  private buildWordContent(details: MinutesOfMeetingDetails): string {
    if (!details) {
      console.error('minutesDetails is null');
      return '';
    }

    const attendanceTable = details.meetingMinutes?.attendanceTable || [];
    const items = details.meetingMinutes?.items || [];

    console.log('Building Word content:', {
      hasDetails: !!details,
      attendanceCount: attendanceTable.length,
      itemsCount: items.length
    });

    // Calculate total pages: items will be distributed across pages (4 items per page)
    const itemsPerPage = 4; // Number of items per page
    const totalPages = items.length > 0 ? Math.ceil(items.length / itemsPerPage) + 1 : 1;

    const page1 = this.buildPage1(details, attendanceTable, totalPages);
    const additionalPages = items.length > 0 ? this.buildAdditionalPages(items, details, totalPages, itemsPerPage) : '';

    const content = page1 + additionalPages;
    console.log('Generated Word content length:', content.length, 'Total pages:', totalPages, 'Items per page:', itemsPerPage);

    return content;
  }

  private buildPage1(details: MinutesOfMeetingDetails, attendanceTable: AttendanceTableMember[], totalPages: number): string {
    // Format date safely
    let formattedDate = '';
    try {
      if (details.basicInfo.meetingDate) {
        const meetingDate = new Date(details.basicInfo.meetingDate);
        if (!isNaN(meetingDate.getTime())) {
          formattedDate = meetingDate.toLocaleDateString('en-GB');
        } else {
          formattedDate = details.basicInfo.meetingDate;
        }
      }
    } catch (e) {
      formattedDate = details.basicInfo.meetingDate || '';
    }

    // Use page-last class only if this is the only page
    const pageClass = totalPages === 1 ? 'page page-last' : 'page';

    return `
      <div class="${pageClass}">
        <div class="header">
          <div class="logo">
            <img src="assets/img/Bidaya-long.png" alt="Bidaya Home Finance" width="180" height="64" style="max-height: 64px; height: auto; max-width: 180px; width: 180px; display: block; object-fit: contain;" onerror="this.style.display='none'">
          </div>
          <div class="header-meta">
            Restricted - محدود الوصول
          </div>
        </div>
        
        <div class="main-title">
          اجتماع لجنة المناقصات للشركة القطرية لشبكة النطاق العريض
        </div>
        
        <div class="item-box">
          <div class="item-header"></div>
          <div class="item-content">
            <div></div>
            <div></div>
          </div>
        </div>
        
        <div class="section-title">
          Qatar National Broadband Network Tender Committee Meeting
        </div>
        
        <table class="meeting-details" style="width: 100%; border-collapse: collapse;">
          <colgroup>
            <col style="width: 25%;">
            <col style="width: 50%;">
            <col style="width: 25%;">
          </colgroup>
          <tr>
            <th style="background-color: #ebebeb; text-align: left; padding: 3px; border: 1px solid #000;">Meeting #</th>
            <td style="text-align: center; padding: 3px; border: 1px solid #000;">${this.escapeHtml(details.basicInfo.meetingId || '')}</td>
            <th style="background-color: #ebebeb; text-align: right; padding: 3px; border: 1px solid #000;">رقم الاجتماع</th>
          </tr>
          <tr>
            <th style="background-color: #ebebeb; text-align: left; padding: 3px; border: 1px solid #000;">Meeting Date</th>
            <td style="text-align: center; padding: 3px; border: 1px solid #000;">${this.escapeHtml(formattedDate)}</td>
            <th style="background-color: #ebebeb; text-align: right; padding: 3px; border: 1px solid #000;">تاريخ الاجتماع</th>
          </tr>
          <tr>
            <th style="background-color: #ebebeb; text-align: left; padding: 3px; border: 1px solid #000;">Meeting Location</th>
            <td style="text-align: center; padding: 3px; border: 1px solid #000;">
              ${this.escapeHtml(details.basicInfo.location || '')}
              ${details.basicInfo.location?.includes('Head Office') ? '<br><span dir="rtl">المبنى الرئيسي للشركة القطرية لشبكة النطاق العريض</span>' : ''}
            </td>
            <th style="background-color: #ebebeb; text-align: right; padding: 3px; border: 1px solid #000;">مكان الاجتماع</th>
          </tr>
          <tr>
            <th style="background-color: #ebebeb; text-align: left; padding: 3px; border: 1px solid #000;">Meeting Start Time</th>
            <td style="text-align: center; padding: 3px; border: 1px solid #000;">${this.escapeHtml(details.basicInfo.meetingTime || '')}</td>
            <th style="background-color: #ebebeb; text-align: right; padding: 3px; border: 1px solid #000;">وقت بدء الاجتماع</th>
          </tr>
          <tr>
            <th style="background-color: #ebebeb; text-align: left; padding: 3px; border: 1px solid #000;">Meeting Duration</th>
            <td style="text-align: center; padding: 3px; border: 1px solid #000;">2 Hours</td>
            <th style="background-color: #ebebeb; text-align: right; padding: 3px; border: 1px solid #000;">مدة الاجتماع</th>
          </tr>
        </table>
        
        <div class="attendance-header" style="margin-top: 20px;">
          Attendance :
        </div>
        <table class="attendance-table">
          <thead>
            <tr>
              <th style="width: 25%;">Member Name</th>
              <th style="width: 15%;">Role</th>
              <th style="width: 15%;">حضور الاجتماع<br>Attendance</th>
              <th style="width: 15%;">الصفة</th>
              <th style="width: 25%;">اسم العضو</th>
              <th style="width: 5%;">الرقم</th>
            </tr>
          </thead>
          <tbody>
            ${attendanceTable.length > 0 ? attendanceTable.map(member => `
              <tr>
                <td>${this.escapeHtml(member.memberNameEnglish || '')}</td>
                <td>${this.escapeHtml(member.roleEnglish || '')}</td>
                <td>${member.attendance ? '☑' : '<span style="color: blue; font-weight: bold;">لم يحضر</span>'}</td>
                <td>${this.escapeHtml(member.roleArabic || '')}</td>
                <td>${this.escapeHtml(member.memberNameArabic || '')}</td>
                <td>(${member.number || ''})</td>
              </tr>
            `).join('') : '<tr><td colspan="6" style="text-align: center; padding: 20px;">No attendance data</td></tr>'}
          </tbody>
        </table>
      </div>
    `;
  }

  private buildAdditionalPages(items: MeetingItem[], details: MinutesOfMeetingDetails, totalPages: number, itemsPerPage: number = 4): string {
    if (!items || items.length === 0) return '';

    // Format date safely
    let formattedDate = '';
    try {
      if (details.basicInfo.meetingDate) {
        const meetingDate = new Date(details.basicInfo.meetingDate);
        if (!isNaN(meetingDate.getTime())) {
          formattedDate = meetingDate.toLocaleDateString('en-GB');
        } else {
          formattedDate = details.basicInfo.meetingDate;
        }
      }
    } catch (e) {
      formattedDate = details.basicInfo.meetingDate || '';
    }

    let pages = '';
    let pageNumber = 2;
    let currentPageItems: MeetingItem[] = [];

    items.forEach((item, index) => {
      currentPageItems.push(item);

      // Check if we should create a new page
      const shouldCreatePage = currentPageItems.length >= itemsPerPage || index === items.length - 1;

      if (shouldCreatePage) {
        // Check if this is the last page
        const isLastPage = pageNumber === totalPages;
        const pageClass = isLastPage ? 'page page-last' : 'page';

        pages += `
          <div class="${pageClass}">
            <div style="text-align: center; color: blue; font-weight: bold; margin-bottom: 20px;">
              Restricted - محدود الوصول
            </div>
        `;

        // Add all items for this page
        currentPageItems.forEach((pageItem, itemIndex) => {
          const decisionEnglish = this.sanitizeRichTextHtml(pageItem.decisionEnglish || '');
          const decisionArabic = this.sanitizeRichTextHtml(pageItem.decisionArabic || '');

          pages += `
            <div class="item-box">
              <div class="item-header">Item #${this.escapeHtml(pageItem.itemNumber || String((pageNumber - 2) * itemsPerPage + itemIndex + 1))} - ${this.escapeHtml(pageItem.subject || pageItem.company || '')}</div>
              <div class="item-content">
                <div>
                  <p style="font-weight: bold; text-decoration: underline; margin-bottom: 5px;">Tender Committee Decision:</p>
                  <div class="decision-rich-text">${decisionEnglish || 'No TC Decision in English...'}</div>
                </div>
                <div style="direction: rtl; text-align: right;">
                  <p style="font-weight: bold; text-decoration: underline; margin-bottom: 5px;">قرار لجنة المناقصات:</p>
                  <div class="decision-rich-text">${decisionArabic || 'No TC Decision in Arabic...'}</div>
                </div>

              </div>
            </div>
          `;

          // Add voting status if available
          if (pageItem.totalVotes && pageItem.totalVotes > 0) {
            pages += `
              <div class="item-box">
                <div class="item-header">Voting Status - حالة التصويت</div>
                <div style="padding: 15px; display: flex; justify-content: space-around; flex-wrap: wrap; gap: 10px;">
                  ${pageItem.acceptedVotes && pageItem.acceptedVotes > 0 ? `<span style="display: inline-flex; align-items: center; gap: 5px; font-weight: bold; color: #00A63E;">✓ ${pageItem.acceptedVotes} Accepted / قبول</span>` : ''}
                  ${pageItem.rejectedVotes && pageItem.rejectedVotes > 0 ? `<span style="display: inline-flex; align-items: center; gap: 5px; font-weight: bold; color: #E63956;">✗ ${pageItem.rejectedVotes} Rejected / رفض</span>` : ''}
                  ${pageItem.deferredVotes && pageItem.deferredVotes > 0 ? `<span style="display: inline-flex; align-items: center; gap: 5px; font-weight: bold; color: #E46A11;">⏰ ${pageItem.deferredVotes} Deferred / تأجيل</span>` : ''}
                </div>
              </div>
            `;
          }
        });

        pages += `
            <div class="footer">
              <span>محضر اجتماع لجنة مناقصات الشركة القطرية لشبكة النطاق العريض #${this.escapeHtml(details.basicInfo.meetingId || '')} بتاريخ ${this.escapeHtml(formattedDate)}</span>
              <span>Page ${pageNumber++} of ${totalPages}</span>
            </div>
          </div>
        `;

        // Reset for next page
        currentPageItems = [];
      }
    });

    return pages;
  }

  private sanitizeRichTextHtml(html: string): string {
    if (!html) {
      return '';
    }

    const template = document.createElement('template');
    template.innerHTML = html;
    const blockedTags = new Set(['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta']);
    const allowedStyleProperties = new Set([
      'color',
      'background-color',
      'font-weight',
      'font-style',
      'text-decoration',
      'text-align',
      'direction'
    ]);

    const cleanNode = (node: Node): void => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType !== Node.ELEMENT_NODE) {
          return;
        }

        const element = child as HTMLElement;
        const tagName = element.tagName.toLowerCase();

        if (blockedTags.has(tagName)) {
          element.remove();
          return;
        }

        Array.from(element.attributes).forEach((attr) => {
          const attrName = attr.name.toLowerCase();
          const attrValue = attr.value || '';

          if (attrName.startsWith('on')) {
            element.removeAttribute(attr.name);
            return;
          }

          if ((attrName === 'href' || attrName === 'src') && /^\s*javascript:/i.test(attrValue)) {
            element.removeAttribute(attr.name);
            return;
          }

          if (attrName === 'style') {
            const safeStyles = attrValue
              .split(';')
              .map(style => style.trim())
              .filter(Boolean)
              .filter((style) => {
                const [property] = style.split(':');
                return allowedStyleProperties.has((property || '').trim().toLowerCase());
              });

            if (safeStyles.length) {
              element.setAttribute('style', safeStyles.join('; '));
            } else {
              element.removeAttribute('style');
            }
          }
        });

        cleanNode(element);
      });
    };

    cleanNode(template.content);
    return template.innerHTML;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private async convertImagesToBase64(container: HTMLElement): Promise<void> {
    const images = container.querySelectorAll('img');
    const imagePromises = Array.from(images).map(async (img: HTMLImageElement) => {
      try {
        // If image already has data URL, skip
        if (img.src.startsWith('data:')) {
          return;
        }

        // Try to load image and convert to base64
        let imageUrl = img.src;

        // If it's a relative path, try to resolve it
        if (imageUrl.startsWith('assets/') || !imageUrl.startsWith('http')) {
          // Try to get the full URL
          const baseUrl = window.location.origin;
          if (!imageUrl.startsWith('http')) {
            imageUrl = `${baseUrl}/${imageUrl}`;
          }
        }

        const response = await fetch(imageUrl);
        if (!response.ok) {
          console.warn(`Failed to load image: ${img.src}`);
          // Try alternative path
          const altPath = `${window.location.origin}/assets/img/Bidaya-long.png`;
          const altResponse = await fetch(altPath);
          if (altResponse.ok) {
            const blob = await altResponse.blob();
            const reader = new FileReader();
            return new Promise<void>((resolve) => {
              reader.onloadend = () => {
                img.src = reader.result as string;
                resolve();
              };
              reader.onerror = () => resolve();
              reader.readAsDataURL(blob);
            });
          }
          return;
        }

        const blob = await response.blob();
        const reader = new FileReader();

        return new Promise<void>((resolve, reject) => {
          reader.onloadend = () => {
            img.src = reader.result as string;
            resolve();
          };
          reader.onerror = () => resolve(); // Continue even if fails
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.warn(`Error converting image ${img.src} to base64:`, error);
        // Continue even if image conversion fails
      }
    });

    await Promise.all(imagePromises);
  }
}

